import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { PACKAGES, fmtMoney } from "@/lib/data";

export const Packages = ({ selected, onSelect, onBook, packages = PACKAGES }) => {
  const active = packages.find((p) => p.id === selected) || packages[1];
  const [galleryIdx, setGalleryIdx] = useState(0);

  const pick = (id) => {
    setGalleryIdx(0);
    onSelect(id);
  };

  return (
    <section id="packages" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="packages-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-14"
      >
        <p className="mb-4 font-mono-x text-xs uppercase tracking-[0.28em] text-amber-400">Three Ways to Glow</p>
        <h2 className="font-display max-w-2xl text-3xl font-bold tracking-tight text-amber-50 sm:text-5xl">
          Pick your package. <span className="italic text-amber-400">Click to see the difference.</span>
        </h2>
        <p className="mt-4 max-w-xl text-base text-emerald-100/70">
          Every package includes professional installation and full January takedown. Reserve your install date with a 50% deposit.
        </p>
      </motion.div>

      <div className="grid gap-5 md:grid-cols-3">
        {packages.map((pkg, i) => {
          const isActive = pkg.id === active.id;
          return (
            <motion.button
              key={pkg.id}
              onClick={() => pick(pkg.id)}
              data-testid={`package-card-${pkg.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              whileHover={{ y: -6 }}
              className={`group relative overflow-hidden rounded-2xl border text-left transition-colors duration-300 ${
                isActive
                  ? "border-amber-400/70 bg-[#122A1E] gold-glow-soft"
                  : "border-amber-500/15 bg-[#0B1D14]/80 hover:border-amber-500/40"
              }`}
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  style={{ objectPosition: "center 72%" }}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D14] via-transparent to-transparent" />
                <span className={`absolute left-4 top-4 rounded-full border px-3 py-1 font-mono-x text-[10px] uppercase tracking-[0.18em] ${
                  pkg.id === "estate"
                    ? "border-amber-400/60 bg-amber-500/90 text-stone-950 font-semibold"
                    : "border-amber-500/30 bg-[#06130D]/80 text-amber-300 backdrop-blur-sm"
                }`}>
                  {pkg.badge}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-end justify-between gap-3">
                  <h3 className="font-display text-xl font-semibold text-amber-50">{pkg.name}</h3>
                  <span className="font-display text-3xl font-black text-amber-400">{fmtMoney(pkg.price)}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-emerald-100/70">{pkg.subtitle}</p>
                <div className="mt-4 flex items-center justify-between border-t border-amber-500/10 pt-4">
                  <span className="font-mono-x text-[11px] uppercase tracking-widest text-emerald-100/50">
                    {fmtMoney(pkg.deposit)} deposit today
                  </span>
                  <span className={`flex items-center gap-1.5 font-mono-x text-[11px] uppercase tracking-widest transition-colors ${isActive ? "text-amber-400" : "text-emerald-100/40 group-hover:text-amber-300"}`}>
                    {isActive ? "Viewing" : "Compare"} <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 overflow-hidden rounded-3xl border border-amber-500/20 bg-[#0B1D14]/90 grain relative"
          data-testid="package-detail-panel"
        >
          <div className="grid lg:grid-cols-2">
            <div className="relative p-5 sm:p-7">
              <div className="clipped-frame relative overflow-hidden rounded-xl">
                <motion.img
                  key={galleryIdx}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  src={active.gallery[galleryIdx]}
                  alt={`${active.name} house mockup`}
                  style={{ objectPosition: "center 72%" }}
                  className="aspect-[4/3] w-full object-cover"
                  data-testid="package-detail-image"
                />
                <span className="absolute bottom-4 left-4 rounded-full bg-[#06130D]/80 px-4 py-1.5 font-mono-x text-[10px] uppercase tracking-[0.2em] text-amber-300 backdrop-blur-md">
                  Your home could look like this
                </span>
              </div>
              <div className="mt-4 flex gap-3">
                {active.gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setGalleryIdx(i)}
                    data-testid={`package-gallery-thumb-${i}`}
                    className={`h-16 w-24 overflow-hidden rounded-lg border-2 transition-all ${
                      i === galleryIdx ? "border-amber-400 gold-glow-soft" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={g} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-amber-500/10 p-6 sm:p-8 lg:border-l lg:border-t-0">
              <p className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">{active.coverage}</p>
              <h3 className="font-display mt-3 text-2xl font-bold text-amber-50 sm:text-3xl">{active.name}</h3>
              <ul className="mt-6 space-y-3">
                {active.included.map((item, i) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.06 }}
                    className="flex items-start gap-3 text-sm text-emerald-100/85"
                  >
                    <Check size={16} className="mt-0.5 shrink-0 text-amber-400" />
                    {item}
                  </motion.li>
                ))}
              </ul>
              <p className="mt-5 text-xs italic leading-relaxed text-emerald-100/50">
                <Sparkles size={12} className="mr-1.5 inline text-amber-400" />
                Best for: {active.bestFor}
              </p>

              <div className="mt-7 rounded-2xl border border-amber-500/20 bg-[#06130D]/70 p-5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-emerald-100/70">Full package price</span>
                  <span className="font-display text-xl font-bold text-amber-50">{fmtMoney(active.price)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-emerald-100/70">Due after installation</span>
                  <span className="text-emerald-100/80">{fmtMoney(active.price - active.deposit)}</span>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-amber-500/15 pt-3">
                  <span className="font-mono-x text-[11px] uppercase tracking-widest text-amber-300">50% deposit to book</span>
                  <span className="font-display text-2xl font-black text-amber-400">{fmtMoney(active.deposit)}</span>
                </div>
              </div>

              <button
                onClick={() => onBook(active.id)}
                data-testid={`package-select-${active.id}`}
                className="mt-6 w-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 py-4 text-sm font-bold uppercase tracking-widest text-stone-950 transition-all hover:from-amber-400 hover:to-amber-300 gold-glow"
              >
                Select This Package — Book Your Spot
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};
